import React, { useMemo } from 'react';
import {
  CommunityApp,
  CommunityNavigation,
  Icon,
  LinkType,
  MenuItemWithAnalytics,
  NavBarExpandableItem,
  NavbarSlideMenuItem,
} from '@atlassian/learning-components';
import { UITrackEventPayload } from '@atlassian/community-library';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useLocation } from 'react-router-dom';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getLoginUrl, getLogoutUrl, getSignUpUrl, getSwitchAccountUrl, isDevelopment } from 'src/services/http/user';
import { sendMarketingUIEvent } from 'src/utils/analytics';
import { getTenant, TENANTS } from 'src/utils/tenant';
import {
  ALM_INTEGRATION_FF,
  CHAMPIONS_PAGE_FF,
  COMMUNITY_ACCOUNT_SWITCHER_FF,
  ContentHubType,
  ENABLEMENT_ACADEMY_IN_ALP_FF,
  JIRA_HUB_FF,
  LandingType,
  NEW_TOP_NAVIGATION_FF,
  ROVO_HUB_FF,
} from 'src/constants';
import {
  getCatalogUrl,
  getCertificationUrl,
  getHubUrl,
  getLandingUrl,
  getMyLearningUrl,
  getUrlPrefix,
  MY_LEARNING_URL_PATH,
} from 'src/utils/getUrl';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';

import { OnDemandMobileMenu } from './OnDemandMobileMenu';
import { AdoptionMobileMenu } from './AdoptionMobileMenu';
import { EnablementNavigation } from './EnablementNavigation';

export const LINKS: { [key: string]: LinkType } = {
  HOME: { text: 'Home', href: '' },
  CATALOG: { text: 'Search catalog', href: getCatalogUrl() },
  BROWSE_ALL: { text: 'Browse all learning', href: getCatalogUrl() },
  LEARNING_PATHS: {
    text: 'Learning paths',
    href: getHubUrl(ContentHubType.LEARNING_PATHS),
  },
  GROW_YOUR_CAREER: {
    text: 'Grow your career',
    href: getHubUrl(ContentHubType.ROLE_BASED),
  },
  PREP_FOR_CERT: {
    text: 'Prepare for a certification',
    href: getHubUrl(ContentHubType.CERT_PREP),
  },
  LIVE_TRAINING: {
    text: 'Live training',
    href: getLandingUrl(LandingType.LIVE_TRAINING),
  },
  CERTIFICATIONS: {
    text: 'Certifications',
    href: getCertificationUrl(),
  },
  ADOPTION_AND_CHANGE_MANAGEMENT_GUIDE: {
    text: 'Adoption and change management guide',
    href: getHubUrl(ContentHubType.ADOPTION_AND_CHANGE_MANAGEMENT),
  },
  ROVO_ADOPTION_GUIDE: {
    text: 'Rovo adoption guide',
    href: getHubUrl(ContentHubType.ROVO),
  },
  JIRA_ADOPTION_GUIDE: {
    text: 'Jira adoption guide',
    href: getHubUrl(ContentHubType.JIRA),
  },
  JIRA_CLOUD_ADOPTION_GUIDE: {
    text: 'Jira Data Center to Cloud adoption guide',
    href: getHubUrl(ContentHubType.JIRA_CLOUD),
  },
  MY_LEARNING: {
    text: 'My learning',
    href: getMyLearningUrl(MyLearningPageContentSections.IN_PROGRESS),
  },
};

export const MY_LEARNING_V2_LINK: LinkType = {
  text: 'My learning',
  href: `/${MY_LEARNING_URL_PATH}/on-demand/${MyLearningPageContentSections.IN_PROGRESS}`,
};

export const withUrlPrefix = (linkType: LinkType): LinkType => ({ ...linkType, href: getUrlPrefix() + linkType.href });

export const AppNavigation: React.FC = () => {
  const { user } = useAtlassianUser();
  const location = useLocation();
  const communityUser = {
    id: user?.account_id || '',
    publicName: user?.name || '',
    avatar: user?.picture || '',
  };

  const showChampions = FeatureGates.checkGate(CHAMPIONS_PAGE_FF) ?? false;
  const showAccountSwitcher = FeatureGates.checkGate(COMMUNITY_ACCOUNT_SWITCHER_FF) ?? false;
  const showNewTopNavigation = FeatureGates.checkGate(NEW_TOP_NAVIGATION_FF) ?? false;
  const almIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF) ?? false;

  const rovoHubEnabled = (isDevelopment() || FeatureGates.checkGate(ROVO_HUB_FF)) ?? false;
  const jiraHubEnabled = (isDevelopment() || FeatureGates.checkGate(JIRA_HUB_FF)) ?? false;
  const showAdoptionSubNav = rovoHubEnabled || jiraHubEnabled; // should depend on individual hub feature gates
  const enablementAcademyEnabled = FeatureGates.checkGate(ENABLEMENT_ACADEMY_IN_ALP_FF);

  /* eslint-disable react-hooks/exhaustive-deps */
  const switchAccountUrl = useMemo(
    () => (showAccountSwitcher ? getSwitchAccountUrl() : ''),
    [location, showAccountSwitcher],
  );

  const loginUrl = useMemo(getLoginUrl, [location]);
  const logoutUrl = useMemo(getLogoutUrl, [location]);
  const signupUrl = useMemo(getSignUpUrl, [location]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Common navigation props shared between tenants
  const commonNavigationProps = {
    signupHref: signupUrl,
    isLoggedIn: !!user,
    user: communityUser,
    loginHref: loginUrl,
    logoutHref: logoutUrl,
    isSticky: false,
    switchHref: switchAccountUrl,
    showNewMenu: showNewTopNavigation,
    onAnalyticsSendEvent: (event: string, payload: UITrackEventPayload): void => {
      sendMarketingUIEvent(payload, user?.account_id);
    },
  };

  // Render tenant-specific navigation
  const currentTenant = getTenant();

  if (currentTenant === TENANTS.ENABLEMENT_ACADEMY && enablementAcademyEnabled) {
    return <EnablementNavigation {...commonNavigationProps} currentApp="enablement-academy" />;
  }

  // Default to Community navigation
  return (
    <CommunityNavigation
      {...commonNavigationProps}
      currentApp={CommunityApp.LEARNING}
      email={user?.email}
      showChampions={showChampions}
      subMenuContentByApp={{
        [CommunityApp.LEARNING]: (
          <>
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.HOME)} type="subMenu" />
            {showNewTopNavigation ? (
              <NavbarSlideMenuItem title="On-demand learning" menuContent={<OnDemandMobileMenu />} type="subMenuItem" />
            ) : (
              <NavBarExpandableItem
                isNested={true}
                headerContent={<span css={onDemandLearningHeaderStyles}>On-demand learning</span>}
                bodyContent={
                  <>
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.BROWSE_ALL)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.LEARNING_PATHS)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.PREP_FOR_CERT)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.GROW_YOUR_CAREER)} type="subMenu" />
                  </>
                }
              />
            )}
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.LIVE_TRAINING)} type="subMenu" />
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.CERTIFICATIONS)} type="subMenu" />
            {showAdoptionSubNav &&
              (showNewTopNavigation ? (
                <NavbarSlideMenuItem title="Adoption" menuContent={<AdoptionMobileMenu />} type="subMenuItem" />
              ) : (
                <NavBarExpandableItem
                  isNested={true}
                  headerContent={<span css={onDemandLearningHeaderStyles}>Adoption</span>}
                  bodyContent={
                    <>
                      {rovoHubEnabled && (
                        <MenuItemWithAnalytics link={withUrlPrefix(LINKS.ROVO_ADOPTION_GUIDE)} type="subMenu" />
                      )}
                      {jiraHubEnabled && (
                        <>
                          <MenuItemWithAnalytics link={withUrlPrefix(LINKS.JIRA_ADOPTION_GUIDE)} type="subMenu" />
                          <MenuItemWithAnalytics link={withUrlPrefix(LINKS.JIRA_CLOUD_ADOPTION_GUIDE)} type="subMenu" />
                        </>
                      )}
                    </>
                  }
                />
              ))}
            {user && (
              <MenuItemWithAnalytics
                link={withUrlPrefix(almIntegrationEnabled ? MY_LEARNING_V2_LINK : LINKS.MY_LEARNING)}
                type="subMenu"
              />
            )}
            {showNewTopNavigation && <SearchCatalogLink />}
          </>
        ),
      }}
    />
  );
};

const SearchCatalogLink = (): React.ReactElement => (
  <div data-testid="search-catalog-link" css={browseAllLearningLinkStyles}>
    <MenuItemWithAnalytics
      link={withUrlPrefix(LINKS.CATALOG)}
      contentBefore={
        <span css={iconWrapperStyles}>
          <Icon type="search" size="medium" />
        </span>
      }
      type="subMenu"
    />
  </div>
);

const onDemandLearningHeaderStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const browseAllLearningLinkStyles = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: token('space.150'),
  margin: `${token('space.100')} 0`,
  paddingLeft: '36px',
  cursor: 'pointer',

  '&:hover a': {
    color: token('color.link'),
  },

  '&:active': {
    backgroundColor: token('color.background.accent.blue.subtlest'),
  },

  '& > span': {
    width: '100%',
  },

  li: {
    padding: 0,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '-0.08px',
    backgroundColor: 'unset',
  },
});

const iconWrapperStyles = css({
  marginRight: token('space.150'),
});
